// ── Outreach request masters (Request Referrals / Request Reviews) ──────────
// Admin-editable master templates for the two standing outreach asks every
// workspace gets: asking a happy client for a referral, and asking for a
// review/testimonial. Same resolve pattern as the reactivation masters
// (shared/reactivationCampaigns.ts): code defaults below + PER-FIELD admin
// overrides stored server-side in system_flags. Lynn edits a master once on
// Admin → Playbooks → Customer Growth; every future workspace seed inherits
// the edit. Copies users already received stay their own.
//
// No emojis anywhere in this copy (owner request, July 2026).

export type OutreachMasterKey = "referral_request" | "review_request";

export const OUTREACH_FIELDS = ["emailSubject", "emailBody"] as const;
export type OutreachField = (typeof OUTREACH_FIELDS)[number];

export type OutreachMaster = {
  key: OutreachMasterKey;
  /** Admin tile label. */
  label: string;
  /** The seeded per-user email template's NAME — the Messaging page's
   * Testimonials/Referrals tiles find templates by this name, and the seeded
   * task notes reference it in words. Not admin-editable. */
  templateName: string;
  emailSubject: string;
  emailBody: string;
};

// The email defaults are the long-standing seeded templates (server/seed.ts
// pre-July-2026); the SMS defaults are their short text-message counterparts.
export const OUTREACH_MASTERS: OutreachMaster[] = [
  {
    key: "referral_request",
    label: "Request Referrals",
    templateName: "Ask for a Referral",
    emailSubject: "Do you know someone?",
    // This general Messaging template deliberately has no referral-link merge
    // tag: a personal link is created only by Referral Generation's contact
    // handoff. Keeping the legacy copy here prevents normal template sends
    // from ever mailing an unresolved {{referralLink}} token.
    emailBody: `Hi {{firstName}},

I hope you're doing well! Working with you has been a real pleasure, and I have a quick favor to ask.

Do you know anyone who might benefit from what I do? A personal referral from someone like you means more than any ad ever could — it helps me reach people I can genuinely help.

If someone comes to mind, here are a couple of easy ways to help:

- Introduce us directly: A quick email or text connecting us is perfect.
- Pass along my info: Feel free to forward this email or share my details with anyone who could use them.

Not sure who to think of? Here are a few prompts to make it easy:

- Who do you know that's facing the same challenge you were before we worked together?
- Is there someone in your network who's mentioned needing this kind of help?
- Who would you trust me to take great care of?

No pressure at all, and I'm grateful for any introduction — big or small. Just reply whenever you get a chance.

Thank you so much!`,
  },
  {
    key: "review_request",
    label: "Request Reviews",
    templateName: "Request a Testimonial",
    emailSubject: "Quick favor {{firstName}}?",
    emailBody: `Hi {{firstName}},

I hope you're doing well! It's been a genuine pleasure working with you, and I'd love your help with something quick.

Would you be open to sharing a short testimonial about your experience? A few honest words from you helps other people feel confident about working with me — and it would mean a lot.

The easiest way is right here — this quick form lets you record a video or type a few words in under a minute:

{{testimonialLink}}

A quick video is preferred! People connect with a real face and voice, but a few typed sentences are perfect too.

Not sure what to say? Here are a few prompts to make it easy:

- What was your situation before we worked together?
- What changed for you, or what results did you see?
- What would you tell someone who's on the fence about working with me?

The best testimonials are specific — a real moment, a number, or a clear "before and after" goes a long way.

No pressure at all, and there's no wrong way to do it. Just open the link above whenever you get a chance.

Thank you so much!`,
  },
];

export const OUTREACH_MASTER_KEYS = OUTREACH_MASTERS.map((m) => m.key);

export function isOutreachMasterKey(v: string): v is OutreachMasterKey {
  return (OUTREACH_MASTER_KEYS as string[]).includes(v);
}

export function getOutreachMaster(key: OutreachMasterKey): OutreachMaster {
  const found = OUTREACH_MASTERS.find((m) => m.key === key);
  if (!found) throw new Error(`Unknown outreach master: ${key}`);
  return found;
}

// ── Admin per-field overrides ────────────────────────────────────────────────
// Stored as one JSON map { key: { field: text } } (system_flags row
// "outreach_masters_overrides_v1" on the server). A blank/absent field
// inherits the code default; storing an override never mutates the defaults.
export type OutreachOverrides = Partial<
  Record<OutreachMasterKey, Partial<Record<OutreachField, string>>>
>;

export function sanitizeOutreachOverrides(raw: unknown): OutreachOverrides {
  const out: OutreachOverrides = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!isOutreachMasterKey(k) || !v || typeof v !== "object") continue;
    const fields: Partial<Record<OutreachField, string>> = {};
    for (const f of OUTREACH_FIELDS) {
      const val = (v as Record<string, unknown>)[f];
      if (typeof val === "string" && val.trim() !== "") fields[f] = val;
    }
    if (Object.keys(fields).length > 0) out[k] = fields;
  }
  return out;
}

/** The resolved master = code default with non-blank admin overrides applied
 * per field. */
export function resolveOutreachMaster(
  key: OutreachMasterKey,
  overrides: OutreachOverrides,
): OutreachMaster {
  const base = getOutreachMaster(key);
  const o = overrides[key] ?? {};
  return {
    ...base,
    emailSubject: o.emailSubject?.trim() ? o.emailSubject : base.emailSubject,
    emailBody: o.emailBody?.trim() ? o.emailBody : base.emailBody,
  };
}
